import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import reportService from '@api/v1/reportService'
import useReadState from '@hooks/useReadState'
import { COLUMN_ALIASES } from '@config/featureFlags'
import ColumnAliasEditor from './ColumnAliasEditor'

/**
 * Entry point 2 of 2 for the firm-vocabulary editor (spec 061 O3, decided by
 * Jordan 2026-08-11): Settings → Report Center → Column names.
 *
 * The inline affordance on a report is the DISCOVERY path — rename a column
 * while you are looking at it. This is the BULK path: work through the firm's
 * vocabulary across several reports in one sitting without hunting for each
 * report first.
 *
 * Both open the SAME `ColumnAliasEditor` against the SAME react-query cache, so
 * a rename made here is visible on the report without a refetch and neither can
 * drift from the other. That single-implementation rule is the whole reason
 * having two entry points is safe.
 *
 * O2 decided the picker offers **all reports**, not a curated subset.
 */
const ColumnAliasSettings = () => {
  const [picked, setPicked] = useState('')

  const tabsQuery = useQuery({
    queryKey: ['reports-list'],
    queryFn: () => reportService.list(''),
    staleTime: 5 * 60_000,
    enabled: COLUMN_ALIASES,
  })
  /*
   * CC-53132. This read used to be `const { data: tabs = [] }` — no `error`, no
   * state. Both halves of that were wrong, and the first one broke a promise
   * another file makes on this file's behalf: `reportService.list`'s docblock
   * names this caller and asserts that a wrong-shaped catalog "surfaces as that
   * query's `error` state, which is exactly what the callers already render"
   * (reportService.js:28-31). It did not. The throw landed in an `error` nobody
   * read, `tabs` fell back to `[]`, and the whole benefit of the CC-50321 guard
   * was discarded here.
   *
   * The second half is the paused read: `isLoading` is `isPending && isFetching`,
   * so a query react-query has PAUSED (default `networkMode: 'online'` while
   * `onlineManager` believes the browser is offline) is neither loading nor
   * errored. `queryFn` is never called, nothing logs, `data` stays undefined —
   * and an admin sitting in Settings → Column names saw a picker offering
   * nothing, i.e. "this firm has no reports whose columns you can rename".
   */
  const { data: tabs, error: tabsError, refetch: refetchTabs } = tabsQuery
  const tabsState = useReadState('column-alias-reports-list', tabsQuery)
  const tabsReady = tabsState === 'ready'

  // Same de-dupe the Queue picker uses: the catalog repeats a report name
  // across tabs, and offering "Aged A/R" three times helps nobody.
  const options = useMemo(() => {
    const seen = new Map()
    for (const t of tabs || []) {
      for (const r of t.reports || []) {
        if (r?.id != null && !seen.has(r.name)) seen.set(r.name, { id: r.id, name: r.name, actionKey: r.actionKey || '' })
      }
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [tabs])

  const chosen = options.find((r) => String(r.id) === String(picked)) || null

  // Columns come from the report's own definition. For a SPECIALIZED report the
  // generic definition carries none — the same 200-with-empty-columns that made
  // the queue modal claim reports had no filters (CC-50275) — so the editor is
  // told plainly rather than shown an empty table it cannot explain.
  const definitionQuery = useQuery({
    queryKey: ['report-def', picked],
    queryFn: () => reportService.getDefinition(picked),
    enabled: COLUMN_ALIASES && !!picked && !chosen?.actionKey,
  })
  const { data: definition, error: definitionError, refetch: refetchDefinition } = definitionQuery
  const definitionState = useReadState('column-alias-report-def', definitionQuery)

  if (!COLUMN_ALIASES) return null

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">Column names</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Rename a report&apos;s columns to your firm&apos;s own words. Everyone at the firm sees them.
        </p>
      </div>
      <div className="p-6">
        {/* The picker only exists once the catalog has actually been read. An
            empty <select> is a statement about the firm, and it must not be made
            on the strength of a read that failed or never happened — so the
            label goes with it (a `htmlFor` pointing at no control is its own
            small a11y defect). `idle` rides with loading: it means the flag is
            off, in which case we returned null above, but leaving it unhandled
            would render this panel headless if anyone ever gates the query on
            something else. */}
        {tabsReady ? (
          <>
            <label htmlFor="alias-report-picker" className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
              Report
            </label>
            <select
              id="alias-report-picker"
              value={picked}
              onChange={(e) => setPicked(e.target.value)}
              className="w-full max-w-md rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12.5px]"
            >
              <option value="">Choose a report…</option>
              {options.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {options.length === 0 && (
              <p className="mt-3 max-w-md text-[12.5px] text-gray-500">
                There are no reports to rename columns on yet.
              </p>
            )}
          </>
        ) : tabsState === 'blocked' ? (
          <div role="alert" className="max-w-md rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-amber-900">
            <p className="font-medium">Can&apos;t reach the server, so we haven&apos;t loaded your reports.</p>
            <p className="mt-1 text-amber-800">
              This is not an empty catalog — your connection looks offline. Check it and try again.
            </p>
            <button
              type="button"
              onClick={() => refetchTabs()}
              className="mt-2.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-[11.5px] font-medium text-amber-900 hover:bg-amber-100"
            >
              Try again
            </button>
          </div>
        ) : tabsState === 'failed' ? (
          /* `reportService.list` writes the user-facing sentence itself (it is the
             layer that knows the catalog was unreadable), so show it verbatim
             rather than paraphrasing it into a second wording. */
          <div role="alert" className="max-w-md rounded-xl border border-red-100 bg-red-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-red-700">
            <p className="font-medium">We couldn&apos;t load your reports: {tabsError?.message}</p>
            <button
              type="button"
              onClick={() => refetchTabs()}
              className="mt-2.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-[11.5px] font-medium text-red-700 hover:bg-red-100"
            >
              Try again
            </button>
          </div>
        ) : (
          <p className="text-[12.5px] text-gray-500">Loading your reports…</p>
        )}

        {chosen?.actionKey ? (
          /* CC-50291: a specialized report's columns exist only inside its own
             grid, so they cannot be listed here — nothing knows them until the
             report has run. Renaming them IS supported, from the report page
             itself, so point there rather than implying it is impossible. */
          <p className="mt-3 max-w-md rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-gray-600">
            {chosen.name} builds its own columns, so they only exist once the report has run.
            {' '}
            <Link to={`/reports/${chosen.id}`} className="font-semibold text-cb-accent-text underline">
              Open {chosen.name}
            </Link>
            {' '}and use <b>Rename columns</b> there.
          </p>
        ) : !picked ? null : definitionState === 'blocked' ? (
          /* Picking a report and getting a blank panel back was the second half
             of CC-53132 here: the `: null` tail below covered a paused read, a
             failed read and a 200 with no definition with the same nothing. */
          <div role="alert" className="mt-3 max-w-md rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-amber-900">
            <p className="font-medium">
              Can&apos;t reach the server, so we haven&apos;t loaded {chosen?.name || 'this report'}&apos;s columns.
            </p>
            <p className="mt-1 text-amber-800">
              This is not a report without columns — your connection looks offline. Check it and try again.
            </p>
            <button
              type="button"
              onClick={() => refetchDefinition()}
              className="mt-2.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-[11.5px] font-medium text-amber-900 hover:bg-amber-100"
            >
              Try again
            </button>
          </div>
        ) : definitionState === 'failed' ? (
          <div role="alert" className="mt-3 max-w-md rounded-xl border border-red-100 bg-red-50 px-3 py-2.5 text-[12.5px] leading-relaxed text-red-700">
            <p className="font-medium">
              We couldn&apos;t load {chosen?.name || 'this report'}&apos;s columns: {definitionError?.message}
            </p>
            <button
              type="button"
              onClick={() => refetchDefinition()}
              className="mt-2.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-[11.5px] font-medium text-red-700 hover:bg-red-100"
            >
              Try again
            </button>
          </div>
        ) : definitionState === 'ready' ? (
          definition ? (
            <ColumnAliasEditor
              reportKey={picked}
              reportName={definition.name || chosen?.name}
              columns={definition.columns || []}
              onClose={() => setPicked('')}
            />
          ) : (
            /* A completed read that carried no definition. Rare, and previously
               indistinguishable from the two failures above — say which one it
               is, because "try again" is the wrong advice for this one. */
            <p className="mt-3 max-w-md text-[12.5px] leading-relaxed text-gray-600">
              {chosen?.name || 'This report'} came back without any column information, so there is
              nothing to rename here.
            </p>
          )
        ) : (
          <p className="mt-3 text-[12.5px] text-gray-500">Loading columns…</p>
        )}
      </div>
    </div>
  )
}

export default ColumnAliasSettings
